import { CustomQualityControlDto, CustomQualityControlType, HotLabEquipmentType, QCType, QcTestResultDto, QualityControlAssignmentDto, VisualQualityControlDto } from '@services/dtos'
import { VisualQualityControlTypeLabels } from '@utils/constants'
import { isNil } from 'lodash'

export function getObservedValue(result: QcTestResultDto): string {
  if (result.observedBooleanValue != null) {
    return result.observedBooleanValue ? 'Oui' : 'Non'
  }
  if (result.observedNumericValue != null) {
    return result.observedNumericValue.toFixed(2).toString()
  }
  if (result.observedTextValue) {
    return result.observedTextValue
  }
  return ''
}

export function getQcTestResultLabel(result: QcTestResultDto): string {
  switch (result.type) {
    case QCType.VISUAL:
      return result.visualQualityControl?.type
        ? VisualQualityControlTypeLabels[result.visualQualityControl.type]
        : ''
    case QCType.CHEMICAL_PURITY:
      return 'Pureté radiochimique'
    case QCType.RADIONUCLIDIC_PURITY:
      return 'Pureté radionuclidique'
    case QCType.PH:
      return 'pH'
    case QCType.CUSTOM:
      return result.customQualityControl?.label ?? ''
    default:
      return ''
  }
}

export function getQualityControlExpectedLabel({ kind, visualQualityControl, customQualityControl, assignment }:
  { kind: QCType, visualQualityControl?: VisualQualityControlDto, customQualityControl?: CustomQualityControlDto, assignment?: QualityControlAssignmentDto }): string {
  switch (kind) {
    case QCType.VISUAL:
      return visualQualityControl?.label || ''
    case QCType.CHEMICAL_PURITY:
      return `≥ ${assignment?.radiochemicalPurityPercent?.toFixed(2)} %`
    case QCType.RADIONUCLIDIC_PURITY:
      return `≥ ${assignment?.radionuclidicPurityPercent?.toFixed(2)} %`
    case QCType.PH: {
      const phMin = assignment?.phMinimum
      const phMax = assignment?.phMaximum
      return !isNil(phMin) && !isNil(phMax)
        ? `${phMin.toFixed(2)} - ${phMax.toFixed(2)}`
        : !isNil(phMin)
          ? `≥ ${phMin.toFixed(2)}`
          : `≤ ${phMax?.toFixed(2)}`
    }
    case QCType.CUSTOM: {
      if (!customQualityControl) {
        return ''
      }
      const unit = customQualityControl.unit ? ` ${customQualityControl.unit}` : ''
      switch (customQualityControl.type) {
        case CustomQualityControlType.NUMERIC: {
          const hasMin = !isNil(customQualityControl.minimum)
          const hasMax = !isNil(customQualityControl.maximum)
          if (hasMin && hasMax) {
            return `${customQualityControl.minimum} - ${customQualityControl.maximum}${unit}`
          }
          if (hasMin) {
            return `≥ ${customQualityControl.minimum}${unit}`
          }
          if (hasMax) {
            return `≤ ${customQualityControl.maximum}${unit}`
          }
          return ''
        }
        case CustomQualityControlType.TEXT:
          return customQualityControl.expectedValue ?? ''
        case CustomQualityControlType.BOOLEAN:
          return !isNil(customQualityControl.expected) ? (customQualityControl.expected ? 'Oui' : 'Non') : ''
        case CustomQualityControlType.LIST:
          return customQualityControl.expectedOption?.label ?? ''
        default:
          return ''
      }
    }
    default:
      return ''
  }
}

export const getEquipmentTypes = ({ kind, customQualityControl }: 
  { kind: QCType, customQualityControl?: CustomQualityControlDto | null }): HotLabEquipmentType[] | null => {
  switch (kind) {
    case QCType.VISUAL: {
      return null
    }
    case QCType.CHEMICAL_PURITY: {
      return [HotLabEquipmentType.TLC_SYSTEM, HotLabEquipmentType.ACTIVIMETER, HotLabEquipmentType.TLC_SCANNER]
    }
    case QCType.RADIONUCLIDIC_PURITY: {
      return [HotLabEquipmentType.GAMMA_SPECTROMETER, HotLabEquipmentType.ACTIVIMETER]
    }
    case QCType.PH: {
      return [HotLabEquipmentType.PH_METER]
    }
    case QCType.CUSTOM: {
      return customQualityControl?.equipmentTypes?.length ? customQualityControl.equipmentTypes : null
    }
    default: {
      return null
    }
  }
}
